import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Button, Form, Table, Alert } from 'react-bootstrap'
import {
  calculateActualAge,
  dateFin,
  isOld,
  isOverMonthOld,
} from '../services/helpfunctions'
import { getU21TournamentEligibility } from '../services/u21utils'

const CopyCandidateListModal = ({
  show,
  onHide,
  sourceCandidate,
  sourceEntries,
  onCopy,
}) => {
  const [teamType, setTeamType] = useState('U21')
  const [name, setName] = useState('')
  const [competition, setCompetition] = useState('CC')
  const [season, setSeason] = useState('')
  const [qualifier, setQualifier] = useState('')
  const [excludedIds, setExcludedIds] = useState(new Set())
  const [isPainting, setIsPainting] = useState(false)
  const [paintToExcluded, setPaintToExcluded] = useState(true)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!show) return

    const nextTeamType =
      sourceCandidate?.TeamType === 'MJ' || sourceCandidate?.TeamType === 'U21'
        ? sourceCandidate.TeamType
        : 'U21'

    setTeamType(nextTeamType)
    setName(sourceCandidate?.Name || '')
    setCompetition(sourceCandidate?.Competition === 'WC' ? 'WC' : 'CC')
    setSeason(
      sourceCandidate?.Season !== undefined && sourceCandidate?.Season !== null
        ? String(sourceCandidate.Season)
        : ''
    )
    setQualifier(sourceCandidate?.Qualifier || '')
    setExcludedIds(new Set())
    setIsPainting(false)
    setPaintToExcluded(true)
    setError('')
    setIsSubmitting(false)
  }, [show, sourceCandidate])

  useEffect(() => {
    if (!show) return

    const stopPainting = () => setIsPainting(false)
    window.addEventListener('mouseup', stopPainting)

    return () => {
      window.removeEventListener('mouseup', stopPainting)
    }
  }, [show])

  const rows = useMemo(() => {
    const parsedSeason = Number(season)

    return (sourceEntries || []).map(entry => {
      const player = entry.player || {}
      const age = calculateActualAge(player)

      let eligibility = '-'
      if (
        teamType === 'U21' &&
        Number.isFinite(parsedSeason) &&
        parsedSeason > 0
      ) {
        const u21 = getU21TournamentEligibility(
          age.years,
          age.days,
          competition,
          parsedSeason
        )
        eligibility = u21?.text || '-'
      } else if (teamType === 'MJ') {
        eligibility = 'MJ'
      }

      const key = String(entry._id || player.playerId)

      return {
        key,
        name: `${player.firstName || ''} ${player.lastName || ''}`.trim(),
        ageText: `${age.years}.${age.days}`,
        eligibility,
        old: isOld(player.dateUpdated),
        monthOld: isOverMonthOld(player.dateUpdated),
        updatedAtText: player.dateUpdated ? dateFin(player.dateUpdated) : '',
      }
    })
  }, [sourceEntries, teamType, competition, season])

  const applyPaint = (rowKey, exclude) => {
    setExcludedIds(prev => {
      const next = new Set(prev)
      if (exclude) {
        next.add(rowKey)
      } else {
        next.delete(rowKey)
      }
      return next
    })
  }

  const handleRowMouseDown = rowKey => {
    const shouldExclude = !excludedIds.has(rowKey)
    setPaintToExcluded(shouldExclude)
    setIsPainting(true)
    applyPaint(rowKey, shouldExclude)
  }

  const handleRowMouseEnter = rowKey => {
    if (!isPainting) return
    applyPaint(rowKey, paintToExcluded)
  }

  const handleSubmit = async event => {
    event.preventDefault()
    setError('')

    const trimmedName = name.trim()
    if (!trimmedName) {
      setError('Listan nimi on pakollinen')
      return
    }

    if (teamType === 'U21') {
      const parsedSeason = Number(season)
      if (!Number.isFinite(parsedSeason) || parsedSeason <= 0) {
        setError('U21-listalle kausi on pakollinen')
        return
      }
      if (!['CC', 'WC'].includes(competition)) {
        setError('Valitse U21-listalle kilpailu (CC tai WC)')
        return
      }
    }

    setIsSubmitting(true)
    try {
      await onCopy({
        teamType,
        name: trimmedName,
        season: teamType === 'U21' ? Number(season) : null,
        competition: teamType === 'U21' ? competition : null,
        qualifier: teamType === 'MJ' ? qualifier.trim() : '',
        excludedIds: Array.from(excludedIds),
      })
      onHide()
    } catch (err) {
      setError(err?.message || 'Kopiointi epäonnistui')
    } finally {
      setIsSubmitting(false)
    }
  }

  const excludedCount = excludedIds.size
  const includedCount = Math.max(rows.length - excludedCount, 0)
  const darkControlStyle = {
    backgroundColor: '#1E2124',
    color: '#FFFFFF',
    borderColor: '#444',
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{
            backgroundColor: '#23272A',
            color: '#FFFFFF',
            borderBottom: '1px solid #3A3F44',
          }}
        >
          <Modal.Title>Kopioi lista</Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{
            backgroundColor: '#2C2F33',
            color: '#FFFFFF',
          }}
        >
          {error && <Alert variant="danger">{error}</Alert>}

          <div className="mb-3" style={{ display: 'flex', gap: '10px' }}>
            <Button
              type="button"
              variant={teamType === 'MJ' ? 'info' : 'outline-secondary'}
              size="sm"
              onClick={() => setTeamType('MJ')}
            >
              MJ
            </Button>
            <Button
              type="button"
              variant={teamType === 'U21' ? 'info' : 'outline-secondary'}
              size="sm"
              onClick={() => setTeamType('U21')}
            >
              U21
            </Button>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Listan nimi</Form.Label>
            <Form.Control
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Anna uuden listan nimi"
              style={darkControlStyle}
              required
            />
          </Form.Group>

          {teamType === 'U21' && (
            <div
              style={{
                display: 'grid',
                gap: '10px',
                gridTemplateColumns: '1fr 1fr',
              }}
            >
              <Form.Group>
                <Form.Label>Kausi</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={season}
                  onChange={e => setSeason(e.target.value)}
                  placeholder="Esim. 42"
                  style={darkControlStyle}
                  required
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Kilpailu</Form.Label>
                <Form.Select
                  value={competition}
                  onChange={e => setCompetition(e.target.value)}
                  style={darkControlStyle}
                >
                  <option value="CC">CC (MMK)</option>
                  <option value="WC">WC (MM)</option>
                </Form.Select>
              </Form.Group>
            </div>
          )}

          {teamType === 'MJ' && (
            <Form.Group className="mt-3">
              <Form.Label>Tarkenne (ei pakollinen)</Form.Label>
              <Form.Control
                value={qualifier}
                onChange={e => setQualifier(e.target.value)}
                placeholder="Esim. A-maa, testi, jne."
                style={darkControlStyle}
              />
            </Form.Group>
          )}

          <hr style={{ borderColor: '#3A3F44' }} />

          <div
            className="mb-2"
            style={{ fontSize: '0.9rem', color: '#B9BBBE' }}
          >
            Maalaa hiirellä rivit harmaaksi. Harmaat pelaajat eivät kopioidu.
            Maalaus saman rivin yli palauttaa sen takaisin aktiiviseksi.
          </div>

          <div
            className="mb-2"
            style={{ fontSize: '0.9rem', color: '#DCDDDE' }}
          >
            Kopioituvat:{' '}
            <strong style={{ color: '#2ecc71' }}>{includedCount}</strong> | Pois
            jätetyt:{' '}
            <strong style={{ color: '#ff8a80' }}>{excludedCount}</strong>
          </div>

          <div
            style={{
              maxHeight: '360px',
              overflowY: 'auto',
              border: '1px solid #3A3F44',
              borderRadius: '4px',
            }}
          >
            <Table size="sm" hover variant="dark" className="mb-0">
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  background: '#23272A',
                  zIndex: 1,
                }}
              >
                <tr>
                  <th>Nimi</th>
                  <th>Ikä</th>
                  <th>Edustuskelpoisuus</th>
                </tr>
              </thead>
              <tbody style={{ userSelect: isPainting ? 'none' : 'auto' }}>
                {rows.map(row => {
                  const isExcluded = excludedIds.has(row.key)
                  const cellStyle = {
                    backgroundColor: isExcluded ? '#9AA0A8' : '#252A31',
                    color: isExcluded ? '#111418' : '#FFFFFF',
                    borderColor: '#3A3F44',
                    fontWeight: isExcluded ? 600 : 400,
                  }

                  return (
                    <tr
                      key={row.key}
                      onMouseDown={() => handleRowMouseDown(row.key)}
                      onMouseEnter={() => handleRowMouseEnter(row.key)}
                      onMouseUp={() => setIsPainting(false)}
                      style={{
                        cursor: 'crosshair',
                        opacity: row.monthOld ? 0.85 : 1,
                        boxShadow: isExcluded
                          ? 'inset 0 0 0 1px #C2C7CE'
                          : 'none',
                        borderLeft: row.old
                          ? '3px solid #e74c3c'
                          : '3px solid #2ecc71',
                      }}
                      title={
                        row.updatedAtText
                          ? `Paivitetty ${row.updatedAtText}`
                          : ''
                      }
                    >
                      <td style={cellStyle}>{row.name || '-'}</td>
                      <td style={cellStyle}>{row.ageText}</td>
                      <td style={cellStyle}>{row.eligibility}</td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        </Modal.Body>

        <Modal.Footer
          style={{
            backgroundColor: '#23272A',
            borderTop: '1px solid #3A3F44',
          }}
        >
          <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
            Peruuta
          </Button>
          <Button type="submit" variant="success" disabled={isSubmitting}>
            {isSubmitting ? 'Kopioidaan...' : 'Kopioi lista'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default CopyCandidateListModal
